import { useCallback, useEffect, useRef, useState } from 'react';

import useProtectActionUnregistered from '@/hooks/useProtectActionUnregistered';
import useStateHandler from '@/hooks/useStateHandler';
import AuthService from '@/services/auth.service';
import MandatoryPopupResultsService from '@/services/mandatoryPopupResults.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import TextInput from '@/shared-components/inputs/components/new/TextInput';
import Text from '@/shared-components/text/components/Text';

import AuthGoogle from './AuthGoogle';

import './styles/AuthLogin.css';

export default function AuthLogin({ setCurrentPage, close }) {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({});
  const formRef = useRef(null);
  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);
  const pendingTurnstileResolveRef = useRef(null);
  const turnstileRenderTimeoutRef = useRef(null);
  const { stateHandler, state } = useStateHandler();
  const { privateActionHandlerUnregistered } = useProtectActionUnregistered({ typeOfProtection: "USER_LIMIT_VIEW", autoOpen: false });
  const turnstileSiteKey = state?.turnstile_site_key ?? null;
  const resetTurnstileWidget = useCallback(() => {
    if (typeof window === "undefined" || !window.turnstile || !turnstileWidgetIdRef.current) {
      return;
    }
    try {
      window.turnstile.reset(turnstileWidgetIdRef.current);
    } catch (resetError) {
      console.warn("Failed to reset Turnstile widget", resetError);
    }
  }, []);

  const resolvePendingTurnstile = useCallback((token) => {
    if (pendingTurnstileResolveRef.current) {
      pendingTurnstileResolveRef.current(token);
      pendingTurnstileResolveRef.current = null;
    }
  }, []);

  const handleTurnstileToken = useCallback((token) => {
    resolvePendingTurnstile(token || "");
  }, [resolvePendingTurnstile]);

  const handleTurnstileError = useCallback(() => {
    resolvePendingTurnstile("");
  }, [resolvePendingTurnstile]);

  const ensureTurnstileWidget = useCallback(() => {
    if (!turnstileSiteKey || !turnstileContainerRef.current || turnstileWidgetIdRef.current) {
      return;
    }
    if (typeof window === "undefined" || !window.turnstile) {
      turnstileRenderTimeoutRef.current = setTimeout(() => {
        turnstileRenderTimeoutRef.current = null;
        ensureTurnstileWidget();
      }, 200);
      return;
    }
    try {
      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: turnstileSiteKey,
        theme: "light",
        size: "invisible",
        callback: handleTurnstileToken,
        "expired-callback": handleTurnstileError,
        "error-callback": handleTurnstileError,
      });
    } catch (renderError) {
      console.warn("Unable to render Turnstile widget", renderError);
    }
  }, [handleTurnstileError, handleTurnstileToken, turnstileSiteKey]);

  useEffect(() => {
    ensureTurnstileWidget();
    return () => {
      if (turnstileRenderTimeoutRef.current) {
        clearTimeout(turnstileRenderTimeoutRef.current);
        turnstileRenderTimeoutRef.current = null;
      }
      if (typeof window !== "undefined" && window.turnstile && turnstileWidgetIdRef.current) {
        try {
          window.turnstile.remove(turnstileWidgetIdRef.current);
        } catch (removeError) {
          console.warn("Failed to remove Turnstile widget", removeError);
        }
      }
      turnstileWidgetIdRef.current = null;
      resolvePendingTurnstile("");
    };
  }, [ensureTurnstileWidget, resolvePendingTurnstile]);

  const acquireTurnstileToken = useCallback(() => {
    if (!turnstileSiteKey) {
      return Promise.resolve("");
    }
    if (typeof window === "undefined" || !window.turnstile || !turnstileWidgetIdRef.current) {
      return Promise.resolve("");
    }
    const existing = window.turnstile.getResponse(turnstileWidgetIdRef.current);
    if (existing) {
      return Promise.resolve(existing);
    }
    return new Promise((resolve) => {
      pendingTurnstileResolveRef.current = resolve;
      try {
        window.turnstile.execute(turnstileWidgetIdRef.current);
      } catch (error) {
        console.warn("Turnstile execution failed", error);
        resolve("");
        pendingTurnstileResolveRef.current = null;
      }
    });
  }, [turnstileSiteKey]);

  const loginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    let turnstileToken = formRef.current?.querySelector("input[name='cf-turnstile-response']")?.value?.trim() ?? "";
    if (!turnstileToken && turnstileSiteKey) {
      turnstileToken = await acquireTurnstileToken();
      if (!turnstileToken) {
        setLoading(false);
        console.warn("Turnstile token unavailable; aborting login.");
        return;
      }
    }

    const { response, data } = await AuthService.login(formData.username, formData.password, turnstileToken);

    if (response.ok) {
      AuthService.initUserSession(stateHandler, setLoading, data);
      // if we haven't completed the onboarding we don't close the dialog
      if (data?.user_session?.user_flags?.onboarding_completed) {
        close();
      }

      privateActionHandlerUnregistered(null, data?.user_session?._id);
      MandatoryPopupResultsService.createMandatoryPopupResult(
        { result: "login_success_email" },
        { useStoredContext: true, clearStoredContext: true }
      ).catch((error) => {
        console.error('Failed to persist mandatory popup login success', error);
      });
    } else if ([400, 404].includes(response.status)) {
      setLoading(false);
      setError({ element: data.error_data.element, error_code: data.error_type });
      resetTurnstileWidget();
    } else {
      setLoading(false);
      resetTurnstileWidget();
    }
  };

  const onFieldChange = (element, property) => {
    setFormData({ ...formData, [property]: element.target.value });
    setError(null);
  };

  return (
    <>
      <div className="auth-login_header-container">
        <p className="auth-login_header-text">Welcome back! Log into your account below.</p>
      </div>
      <form onSubmit={loginSubmit} className="auth-login__form" ref={formRef}>
        <TextInput
          error={error}
          label="Username or email"
          placeholder='Username or email'
          name="user_email"
          value={formData.username}
          element="user_email"
          onChange={(e) => onFieldChange(e, "username")}
        />
        <TextInput
          error={error}
          label="Password"
          placeholder='Password'
          name="user_password"
          value={formData.password}
          element="user_password"
          type="password"
          onChange={(e) => onFieldChange(e, "password")}
        />
        <Text
          className="auth-login__forgot-password element-non-selectable font-size-small"
          onClick={() => setCurrentPage("forgot")}>Forgot your password?</Text>

        {turnstileSiteKey && (
          <div className="auth-login__turnstile">
            <div
              className="auth-login__turnstile-widget"
              ref={turnstileContainerRef}
            />
          </div>
        )}

        <ContinueButton loading={loading} turnOff={error || !formData.username || !formData.password}>
                    Continue
        </ContinueButton>
      </form>
      <AuthGoogle
        className='auth-regular-signup__google-button'
        close={close}
        textButton='Log in with Google'
        resultType="login_success_google"
      />
    </>
  );
}
